import { mdiArrowDown, mdiArrowUp } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { NavLink, useBlocker } from 'react-router-dom';
import {
    CellProps,
    HeaderProps,
    Row,
    SortingRule,
    TableInstance,
    TableOptions,
    useAsyncDebounce,
    useExpanded,
    useResizeColumns,
    useRowState,
    useSortBy,
    useTable,
} from 'react-table';
import { TooltipTransitionComponent, Spinner } from '../base';
import {
    ActiveFilters,
    ColumnFilter,
    Filters,
    IColumnFiltersConfig,
    ICustomFilter,
    IRowAction,
    Loader,
    Placeholder,
    RowActions,
    Settings,
    TableExport,
} from './components';
import { RowSelectCell } from './components/cells/row-select';
import { ITableFetchOptions } from './data-table';
import { InfiniteScrollDataTablePresenter } from './infinite-scroll-data-table.presenter';
import * as S from './infinite-scroll-data-table.styles';
import { useFlexLayout } from './plugins';

export interface IInfiniteScrollDataTableProps<D extends object = any> {
    className?: string;
    presenter: InfiniteScrollDataTablePresenter<any>;
    isLoading?: boolean;
    hideSettings?: boolean;
    hideNavigation?: boolean;
    hideFiltering?: boolean;
    hideSelectAll?: boolean;
    disableSorting?: boolean;
    selectableRows?: boolean;
    pageSizeOptions?: number[];
    showExport?: boolean;
    summaryColumns?: string[];
    columnFiltersConfig?: IColumnFiltersConfig;
    onFetch?: (options: ITableFetchOptions) => void;
    onRowClick?: (row: Row<D>) => void;
    getRowHref?: (row: Row<D>) => string;
    renderRowSubComponent?: (row: Row<D>) => React.ReactElement | null;
    renderToolbar?: (rows: Row<D>[], prepareRow: (row: Row<D>) => void) => React.ReactElement;
    renderRowWrapper?: (row: Row<D>, renderRow: React.ReactElement) => React.ReactElement;
    rowClass?: (row: Row<D>) => string;
    NotFoundComponent?: React.ComponentType;
    loadMoreThreshold?: number;
    onLoadMore?: (options: ITableFetchOptions) => void;
    enableInfiniteScroll?: boolean;
    rowActions?: IRowAction<D>[];
    selectedRowId?: string | null;
    onSelectedRowChange?: (rowId: string | null) => void;
}

const FETCH_DEBOUNCE = 500;
const DEFAULT_LOAD_MORE_THRESHOLD = 200;

const defaultColumn = {
    minWidth: 50,
    width: 100,
    maxWidth: 400,
};

export const InfiniteScrollDataTable: React.FunctionComponent<IInfiniteScrollDataTableProps> = observer(props => {
    const {
        className,
        presenter,
        isLoading,
        hideSettings,
        hideNavigation,
        hideFiltering,
        hideSelectAll,
        disableSorting,
        selectableRows,
        pageSizeOptions,
        summaryColumns,
        columnFiltersConfig,
        onFetch,
        onRowClick,
        getRowHref,
        showExport,
        renderRowSubComponent,
        renderToolbar,
        renderRowWrapper,
        rowClass,
        NotFoundComponent,
        loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
        onLoadMore,
        enableInfiniteScroll = true,
        rowActions,
        selectedRowId: controlledSelectedRowId,
        onSelectedRowChange,
    } = props;

    const [internalSelectedRowId, setInternalSelectedRowId] = React.useState<string | null>(null);
    
    // Use controlled state if provided, otherwise use internal state
    const selectedRowId = controlledSelectedRowId !== undefined ? controlledSelectedRowId : internalSelectedRowId;
    const setSelectedRowId = React.useCallback((rowId: string | null) => {
        if (onSelectedRowChange) {
            onSelectedRowChange(rowId);
        } else {
            setInternalSelectedRowId(rowId);
        }
    }, [onSelectedRowChange]);
    
    const defaultFetchOptions = React.useMemo(() => presenter.getTableDefaults(), [presenter]);

    const {
        columns,
        hiddenColumnIds,
        data,
        dataTotal,
        controlledPageSize,
        validFilters,
        selectedIds,
        saveTableDefaults,
        saveFilters,
        onColumnToggle,
        onSelectedUpdate,
        onPageSizeUpdate,
        accumulatedData,
        currentPage,
        hasMore,
        isLoadingMore,
        incrementPage,
        resetData,
        setHasMore,
        setIsLoadingMore,
    } = presenter;

    const sentinelRef = useRef<HTMLDivElement>(null);
    const previousFiltersRef = useRef<ICustomFilter[]>([]);
    const previousSortRef = useRef<SortingRule<object>[]>([]);
    const isFirstFetch = useRef(true);

    const defaultSortColumn = columns.find(column => !!column.defaultSort);
    const defaultSort = defaultSortColumn?.defaultSort ? [defaultSortColumn.defaultSort] : [];

    const defaultSortValue = _.isEmpty(defaultFetchOptions?.sortBy) ? defaultSort : defaultFetchOptions?.sortBy;
    const sortByValue = disableSorting ? [] : defaultSortValue;

    // Memoize tableProps to regenerate rows only when we change values that can invoke rerender
    const tableProps: TableOptions<any> = useMemo(
        () => ({
            getRowId: (row: Row<any>, i: number) => row.id || i.toString(),
            defaultColumn: { ...defaultColumn },
            columns,
            data: accumulatedData, // Use accumulated data instead of data
            initialState: {
                pageIndex: 0,
                pageSize: controlledPageSize,
                sortBy: sortByValue,
                hiddenColumns: hiddenColumnIds,
            },
            useControlledState: state => {
                return React.useMemo(
                    () => ({
                        ...state,
                        pageSize: controlledPageSize,
                    }),
                    [state, controlledPageSize],
                );
            },
            pageCount: -1, // Disable pagination
            manualSortBy: true,
            manualPagination: false, // Disable pagination for infinite scroll
            disableSortRemove: true,
            disableSortBy: disableSorting,
            autoResetExpanded: false,
            autoResetRowState: false,
        }),
        [
            accumulatedData,
            controlledPageSize,
            columns,
            selectedIds,
            disableSorting,
            defaultFetchOptions,
            hiddenColumnIds,
            sortByValue,
        ],
    );

    const tableInstance = useTable(
        tableProps,
        useSortBy,
        useExpanded,
        useResizeColumns,
        useFlexLayout,
        useRowState,
        hooks => {
            if (selectableRows) {
                hooks.visibleColumns.push(visibleColumns => [
                    {
                        id: 'selection',
                        width: 'auto',
                        minWidth: 60,
                        Header: (headerProps: HeaderProps<any>) => {
                            if (hideSelectAll) {
                                return null;
                            }

                            const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                const checked = event.target.checked;

                                const columnIds: Record<string, boolean> = headerProps.flatRows.reduce(
                                    (acc, row) => ({
                                        ...acc,
                                        [row.id]: checked,
                                    }),
                                    {},
                                );

                                const updatedSelectedIds = _.pickBy<boolean>({ ...selectedIds, ...columnIds }, val => val);

                                onSelectedUpdate(updatedSelectedIds);
                            };

                            return (
                                <RowSelectCell
                                    checked={headerProps.flatRows.every(row => selectedIds[row.id])}
                                    onChange={handleChange}
                                />
                            );
                        },
                        Cell: ({ row }: CellProps<any>) => {
                            const id = row.original.id;

                            const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                const checked = event.target.checked;
                                const updatedSelectedIds = _.pickBy<boolean>({ ...selectedIds, [id]: checked }, val => val);

                                onSelectedUpdate(updatedSelectedIds);
                            };

                            return <RowSelectCell checked={selectedIds[id]} showBorder={true} onChange={handleChange} />;
                        },
                    },
                    ...visibleColumns,
                ]);
            }

            // Wrap Subject column Cell to add ellipsis
            hooks.visibleColumns.push(visibleColumns => 
                visibleColumns.map(column => {
                    if (column.id === 'Subject') {
                        const OriginalCell = column.Cell;
                        return {
                            ...column,
                            Cell: (cellProps: CellProps<any>) => {
                                // Get original subject value for tooltip
                                const originalValue = cellProps.value || '';
                                
                                // Render the original cell content using React.createElement
                                const cellContent = OriginalCell 
                                    ? React.createElement(OriginalCell as any, cellProps)
                                    : cellProps.value;
                                
                                return (
                                    <S.SubjectCell title={typeof originalValue === 'string' ? originalValue : ''}>
                                        {cellContent}
                                    </S.SubjectCell>
                                );
                            }
                        };
                    }
                    return column;
                })
            );
        },
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setHiddenColumns,
        state: { sortBy, columnResizing } = tableInstance.state as any,
    } = tableInstance;

    const summaryValues = useMemo(() => {
        if (!summaryColumns || summaryColumns.length === 0 || rows.length === 0) {
            return {};
        }

        const sums: Record<string, number> = {};

        summaryColumns.forEach(columnId => {
            const column = columns.find(col => col.id === columnId);
            if (!column || !column.accessor) {
                return;
            }

            const sum = rows.reduce((total, row) => {
                const value = _.get(row.original, column.accessor as string);
                
                let numericValue: number;
                if (value && typeof value === 'object' && 'amount' in value) {
                    numericValue = parseFloat(value.amount);
                } else if (typeof value === 'number') {
                    numericValue = value;
                } else {
                    numericValue = parseFloat(value);
                }
                
                return total + (isNaN(numericValue) ? 0 : numericValue);
            }, 0);

            sums[columnId] = sum;
        });

        return sums;
    }, [rows, summaryColumns, columns]);

    useEffect(() => {
        setHiddenColumns(hiddenColumnIds);
    }, [hiddenColumnIds]);

    // Debounce our onFetchData call
    const handleFetchDebounce = onFetch && useAsyncDebounce(onFetch, FETCH_DEBOUNCE);

    const fetchData = useCallback(
        (options: ITableFetchOptions): void => {
            if (!handleFetchDebounce) {
                return;
            }

            // For the first fetch we shouldn't use debounce
            if (isFirstFetch.current) {
                isFirstFetch.current = false;
                onFetch?.(options);
                return;
            }

            handleFetchDebounce(options);
        },
        [handleFetchDebounce, onFetch],
    );

    const handleColumnFilterChange = useCallback(
        (filterId: string, filter: ICustomFilter | undefined) => {
            if (filterId === 'filterCreatedCombined' || filterId === 'filterUpdatedCombined') {
                const isCreated = filterId === 'filterCreatedCombined';
                const authorFilterId = 'filterAuthor';
                const dateFilterId = isCreated ? 'filterCreated' : 'filterUpdated';

                if (filter && filter.value) {
                    const combinedValue = filter.value as {
                        author?: { method: any; value: string };
                        date?: { method: any; value: Date };
                    };

                    presenter.findAndRemoveFilter({ filterId: authorFilterId });
                    presenter.findAndRemoveFilter({ filterId: dateFilterId });

                    if (combinedValue.author && combinedValue.author.value) {
                        presenter.filters.push({
                            filterId: authorFilterId,
                            filterMethod: combinedValue.author.method,
                            filterType: 'String',
                            value: combinedValue.author.value,
                            label: isCreated ? 'Author' : 'Last Author',
                        } as ICustomFilter);
                    }

                    if (combinedValue.date && combinedValue.date.value) {
                        presenter.filters.push({
                            filterId: dateFilterId,
                            filterMethod: combinedValue.date.method,
                            filterType: 'DateTime',
                            value: combinedValue.date.value,
                            label: isCreated ? 'Created' : 'Updated',
                        } as ICustomFilter);
                    }
                } else {
                    presenter.findAndRemoveFilter({ filterId: authorFilterId });
                    presenter.findAndRemoveFilter({ filterId: dateFilterId });
                }
            } else {
                presenter.onColumnFilterChange(filterId, filter);
            }
        },
        [presenter],
    );

    // Detect filter/sort changes and reset accumulated data
    useEffect(() => {
        const filtersChanged = !_.isEqual(validFilters, previousFiltersRef.current);
        const sortChanged = !_.isEqual(sortBy, previousSortRef.current);

        if (filtersChanged || sortChanged) {
            mobx.runInAction(() => {
                resetData();
                presenter.setCurrentPage(0);
            });
            previousFiltersRef.current = [...validFilters];
            previousSortRef.current = [...sortBy];

            if (onFetch) {
                fetchData({
                    pageIndex: 0,
                    pageSize: controlledPageSize,
                    sortBy,
                    validFilters,
                });
            }
        } else {
            previousFiltersRef.current = [...validFilters];
            previousSortRef.current = [...sortBy];
        }
    }, [validFilters, sortBy, resetData, controlledPageSize, onFetch, fetchData]);

    // Initial fetch
    useEffect(() => {
        if (isFirstFetch.current && onFetch) {
            fetchData({
                pageIndex: 0,
                pageSize: controlledPageSize,
                sortBy,
                validFilters,
            });
            saveTableDefaults({ pageIndex: 0, pageSize: controlledPageSize, sortBy });
            saveFilters(validFilters);
        }
    }, []); // Only run on mount

    // Reset isLoadingMore when data updates (after fetch completes)
    useEffect(() => {
        if (isLoadingMore && !isLoading) {
            setIsLoadingMore(false);
        }
    }, [isLoading, isLoadingMore, setIsLoadingMore]);

    const previousDataLengthRef = useRef(0);
    useEffect(() => {
        if (isLoadingMore && accumulatedData.length > previousDataLengthRef.current) {
            setIsLoadingMore(false);
        }
        previousDataLengthRef.current = accumulatedData.length;
    }, [accumulatedData.length, isLoadingMore, setIsLoadingMore]);

    // IntersectionObserver for scroll detection
    useEffect(() => {
        if (!enableInfiniteScroll || !sentinelRef.current || !hasMore || isLoadingMore || !onFetch) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                const [entry] = entries;
                // Only load more if we have data (prevents fetching page 1 before page 0 loads)
                if (entry.isIntersecting && hasMore && !isLoadingMore && !isLoading && accumulatedData.length > 0) {
                    setIsLoadingMore(true);
                    incrementPage();

                    const fetchOptions: ITableFetchOptions = {
                        pageIndex: currentPage + 1,
                        pageSize: controlledPageSize,
                        sortBy,
                        validFilters,
                    };

                    if (onLoadMore) {
                        onLoadMore(fetchOptions);
                    } else {
                        fetchData(fetchOptions);
                    }
                }
            },
            {
                rootMargin: `${loadMoreThreshold}px`,
            },
        );

        const currentSentinel = sentinelRef.current;
        observer.observe(currentSentinel);

        return () => {
            observer.unobserve(currentSentinel);
        };
    }, [
        enableInfiniteScroll,
        hasMore,
        isLoadingMore,
        isLoading,
        currentPage,
        controlledPageSize,
        sortBy,
        validFilters,
        onFetch,
        onLoadMore,
        loadMoreThreshold,
        incrementPage,
        setIsLoadingMore,
        fetchData,
        accumulatedData.length,
    ]);

    const placeholder = dataTotal === 0 && !!isLoading;
    const loading = dataTotal > 0 && !!isLoading;
    const emptyState = accumulatedData.length === 0 && !isLoading;

    const isSelected = Object.values(selectedIds).some(id => !!id);

    // Safely use useBlocker: when rendered outside of a data router (e.g. inside a modal),
    // React Router throws. Guard with try/catch and fall back to a no-op blocker.
    let blocker: any;
    try {
        blocker = useBlocker(({ currentLocation, nextLocation }) =>
            isSelected && !!getRowHref && currentLocation.pathname !== nextLocation.pathname,
        );
    } catch (err) {
        blocker = { state: 'unblocked', proceed: () => {}, reset: () => {} };
    }

    useEffect(() => {
        if (blocker.state === "blocked") {
            if (window.confirm("There are some items in the table selected. Do you still want to navigate to the another page?")) {
                blocker.proceed?.();
            } else {
                blocker.reset?.();
            }
        }
    }, [blocker]);

    return (
        <S.TableWrapper className={className}>
            {!hideFiltering && (
                <>
                    <S.ActiveFiltersPanel>
                        <ActiveFilters presenter={presenter} />
                    </S.ActiveFiltersPanel>
                </>
            )}
            {(!hideFiltering || !hideNavigation || !hideSettings) && (
                <S.ControlsPanel>
                    {!hideFiltering && !columnFiltersConfig && <Filters presenter={presenter} />}
                    {!hideNavigation && (
                        <S.NavigationContainer>
                            {!hideSettings && (
                                <Settings
                                    columns={columns}
                                    hiddenColumnIds={hiddenColumnIds}
                                    onColumnToggle={onColumnToggle}
                                    variant="small"
                                />
                            )}
                            {!!showExport && <TableExport rows={rows} prepareRow={prepareRow} />}
                        </S.NavigationContainer>
                    )}
                </S.ControlsPanel>
            )}
            {renderToolbar?.(rows, prepareRow)}
            {placeholder ? (
                <Placeholder />
            ) : (
                <S.TableContainer>
                    {loading && <Loader loading={loading} />}
                    <S.Table {...getTableProps()}>
                        <S.TableHead>
                            {headerGroups.map((headerGroup, hi) => (
                                <S.TableRow {...headerGroup.getHeaderGroupProps()} key={hi}>
                                    {headerGroup.headers.map((column, ci) => {
                                        const description = _.get(column, 'description');
                                        const columnId = column.id;
                                        const hasColumnFilter = columnFiltersConfig && columnId && columnFiltersConfig[columnId];
                                        // Get the filterId from config, fallback to columnId if not specified
                                        const filterId = hasColumnFilter ? (columnFiltersConfig[columnId].filterId || columnId) : columnId;
                                        let currentFilter = hasColumnFilter ? presenter.getColumnFilter(filterId) : undefined;

                                        if (!currentFilter && (filterId === 'filterCreatedCombined' || filterId === 'filterUpdatedCombined')) {
                                            const isCreated = filterId === 'filterCreatedCombined';
                                            const authorFilterId = 'filterAuthor';
                                            const dateFilterId = isCreated ? 'filterCreated' : 'filterUpdated';

                                            const authorFilter = presenter.getColumnFilter(authorFilterId);
                                            const dateFilter = presenter.getColumnFilter(dateFilterId);

                                            if (authorFilter || dateFilter) {
                                                currentFilter = {
                                                    filterId: filterId,
                                                    filterMethod: undefined,
                                                    filterType: 'String',
                                                    value: {
                                                        author: authorFilter ? {
                                                            method: authorFilter.filterMethod,
                                                            value: authorFilter.value,
                                                        } : undefined,
                                                        date: dateFilter ? {
                                                            method: dateFilter.filterMethod,
                                                            value: dateFilter.value,
                                                        } : undefined,
                                                    },
                                                } as any as ICustomFilter;
                                            }
                                        }

                                        return (
                                            <S.TableHeader
                                                canSort={(column as any).canSort}
                                                isSorted={(column as any).isSorted}
                                                data-sorted={(column as any).isSorted ? "true" : "false"}
                                                {...column.getHeaderProps()}
                                                key={ci}
                                                className="table-header-with-filter">
                                                <div {...(column as any).getSortByToggleProps({ title: undefined })}>
                                                    <Tooltip
                                                        placement="top"
                                                        TransitionComponent={TooltipTransitionComponent}
                                                        title={<span>{description}</span>}
                                                        open={description ? undefined : false}
                                                        enterTouchDelay={0}>
                                                        <span>{column.render('Header')}</span>
                                                    </Tooltip>
                                                    {hasColumnFilter && (
                                                        <ColumnFilter
                                                            columnId={columnId}
                                                            columnLabel={typeof column.Header === 'string' ? column.Header : columnId}
                                                            filterConfig={columnFiltersConfig[columnId]}
                                                            currentFilter={currentFilter}
                                                            onApply={(filter) => handleColumnFilterChange(filterId, filter)}
                                                        />
                                                    )}
                                                    {(column as any).isSorted && (
                                                        <SortIcon isSortedDesc={!!(column as any).isSortedDesc} />
                                                    )}
                                                </div>

                                                <div
                                                    {...(column as any).getResizerProps()}
                                                    className={`resizer ${(column as any).isResizing ? 'isResizing' : ''}`}
                                                />
                                            </S.TableHeader>
                                        );
                                    })}
                                </S.TableRow>
                            ))}
                        </S.TableHead>
                        <S.TableBody {...getTableBodyProps()}>
                            <TableBody
                                rows={rows}
                                prepareRow={prepareRow}
                                onRowClick={onRowClick}
                                getRowHref={getRowHref}
                                renderRowSubComponent={renderRowSubComponent}
                                renderRowWrapper={renderRowWrapper}
                                rowClass={rowClass}
                                isSelected={isSelected}
                                columnResizing={columnResizing}
                                summaryColumns={summaryColumns}
                                summaryValues={summaryValues}
                                headerGroups={headerGroups}
                                rowActions={rowActions}
                                selectedRowId={selectedRowId}
                                setSelectedRowId={setSelectedRowId}
                            />
                            {enableInfiniteScroll && isLoadingMore && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', width: '100%' }}>
                                    <Spinner />
                                </div>
                            )}
                            {/* Sentinel element for infinite scroll */}
                            {enableInfiniteScroll && (
                                <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />
                            )}
                            {enableInfiniteScroll && !hasMore && !isLoadingMore && accumulatedData.length > 0 && (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#666', width: '100%' }}>
                                    No more data
                                </div>
                            )}
                        </S.TableBody>
                    </S.Table>
                </S.TableContainer>
            )}
            {emptyState && (NotFoundComponent ? <NotFoundComponent /> : <EmptyState />)}
        </S.TableWrapper>
    );
});

type TableProps = Pick<TableInstance, 'rows' | 'prepareRow'> &
    Pick<IInfiniteScrollDataTableProps, 'onRowClick' | 'getRowHref' | 'renderRowSubComponent' | 'renderRowWrapper' | 'rowClass' | 'summaryColumns' | 'rowActions'> & {
        isSelected?: boolean;
        columnResizing: any;
        summaryValues: Record<string, number>;
        headerGroups: any[];
        selectedRowId: string | null;
        setSelectedRowId: (id: string | null) => void;
    };

interface TableRowComponentProps {
    row: Row<any>;
    onRowClick?: (row: Row) => void;
    getRowHref?: (row: Row) => string;
    renderRowSubComponent?: (row: Row) => React.ReactElement | null;
    rowClass?: (row: Row) => string;
    rowActions?: IRowAction[];
    selectedRowId: string | null;
    setSelectedRowId: (id: string | null) => void;
}

const TableRowComponent = React.memo<TableRowComponentProps>(({ 
    row, 
    onRowClick, 
    getRowHref, 
    renderRowSubComponent, 
    rowClass, 
    rowActions,
    selectedRowId,
    setSelectedRowId 
}) => {
    const [isRowHovered, setIsRowHovered] = React.useState(false);
    const hoverable = !!onRowClick || !!getRowHref;
    const { key, ...rowProps } = row.getRowProps();
    const isSelected = selectedRowId === row.id;
    
    const handleRowClick = (e: React.MouseEvent) => {
        setSelectedRowId(row.id);
        onRowClick?.(row);
    };
    
    const handleRowClass = rowClass ? rowClass(row) : undefined;

    return (
        <React.Fragment>
            <S.TableRow
                key={key}
                className={handleRowClass}
                onClick={handleRowClick}
                onMouseEnter={() => setIsRowHovered(true)}
                onMouseLeave={() => setIsRowHovered(false)}
                hoverable={hoverable}
                selected={isSelected}
                {...rowProps}>
                {row.cells.map((cell, ci) => {
                    return (
                        <S.TableCell {...cell.getCellProps()} key={ci}>
                            {cell.render('Cell')}
                        </S.TableCell>
                    );
                })}
                {rowActions && rowActions.length > 0 && (
                    <RowActions row={row} actions={rowActions} isVisible={isRowHovered} isRead={row.original.read === true} />
                )}
            </S.TableRow>
            {(row as any).isExpanded && <S.TableRow>{renderRowSubComponent && renderRowSubComponent(row)}</S.TableRow>}
        </React.Fragment>
    );
});

const TableBody = React.memo<TableProps>(props => {
    const {
        rows,
        onRowClick,
        getRowHref,
        renderRowSubComponent,
        renderRowWrapper,
        rowClass,
        prepareRow,
        isSelected,
        summaryColumns,
        summaryValues,
        headerGroups,
        rowActions,
        selectedRowId,
        setSelectedRowId,
    } = props;

    const showSummary = summaryColumns && summaryColumns.length > 0 && rows.length > 0;

    return (
        <div>
            {rows.map((row, ri) => {
                prepareRow(row);
                const handleNavLinkClick = () => {
                    setSelectedRowId(row.id);
                };
                
                const rowElement = (
                    <TableRowComponent
                        row={row}
                        onRowClick={onRowClick}
                        getRowHref={getRowHref}
                        renderRowSubComponent={renderRowSubComponent}
                        rowClass={rowClass}
                        rowActions={rowActions}
                        selectedRowId={selectedRowId}
                        setSelectedRowId={setSelectedRowId}
                    />
                );

                if (getRowHref) {
                    return (
                        <NavLink to={getRowHref(row)} key={row.id || ri} onClick={handleNavLinkClick}>
                            {!!renderRowWrapper ? renderRowWrapper(row, rowElement) : rowElement}
                        </NavLink>
                    );
                }

                return (
                    <React.Fragment key={row.id || ri}>
                        {!!renderRowWrapper ? renderRowWrapper(row, rowElement) : rowElement}
                    </React.Fragment>
                );
            })}
            {showSummary && <SummaryRow headerGroups={headerGroups} summaryValues={summaryValues} />}
        </div>
    );
});

const SummaryRow = React.memo<{
    headerGroups: any[];
    summaryValues: Record<string, number>;
}>(({ headerGroups, summaryValues }) => {
    const headerGroup = headerGroups[0];
    if (!headerGroup) {
        return null;
    }

    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
    return (
        <S.TableSummaryRow key={key} {...headerGroupProps}>
            {headerGroup.headers.map((column: any, ci: number) => {
                const columnId = column.id;
                const summaryValue = summaryValues[columnId];
                
                let displayValue: string | number = '';
                if (summaryValue !== undefined) {
                    displayValue = `$${summaryValue.toFixed(2)}`;
                }

                const { key: colKey, ...colHeaderProps } = column.getHeaderProps();
                return (
                    <S.TableSummaryCell key={colKey} {...colHeaderProps}>
                        {displayValue}
                    </S.TableSummaryCell>
                );
            })}
        </S.TableSummaryRow>
    );
});

const EmptyState = React.memo(() => (
    <S.EmptyContainer>
        <S.EmptyTitle>Nothing to show</S.EmptyTitle>
    </S.EmptyContainer>
));

const SortIcon = React.memo<{ isSortedDesc: boolean }>(({ isSortedDesc }) =>
    isSortedDesc ? <S.SortIcon path={mdiArrowDown} size={0.5} /> : <S.SortIcon path={mdiArrowUp} size={0.5} />,
);

